import { cn } from 'cn';
import type { TranslationReading } from '@/features/reader/types';

/**
 * The six provenance badges.
 *
 * Each is an icon **plus a word**, because colour is never allowed to be the
 * only signal — and because the distinction that matters most here is one
 * colour cannot carry: "AI translation · unreviewed" has to say *unreviewed*
 * out loud. Informational only: never clickable, never more than one source
 * badge and one translation badge together.
 *
 * Reviewed is blue on purpose. It sits off the teal/green axis so it is never
 * mistaken for Publisher-sourced.
 */

type BadgeKind =
  | 'publisher'
  | 'user'
  | 'official'
  | 'reviewed'
  | 'ai'
  // A person wrote it and nobody has checked it. Distinct from `ai`, which
  // is a different claim about who produced the text.
  | 'human'
  | 'stale'
  // Nothing has been translated yet. Distinct from `stale`, which means a
  // translation exists but its source has moved on.
  | 'none';

const LABELS: Record<BadgeKind, string> = {
  publisher: 'Publisher-sourced',
  user: 'User-submitted',
  official: 'Official translation',
  reviewed: 'Reviewed translation',
  // The word is part of the label, not a tooltip.
  ai: 'AI translation · unreviewed',
  human: 'Human translation · unreviewed',
  stale: 'Stale',
  // Said out loud rather than left blank. An absent translation badge reads
  // as "unknown"; this reads as "none, and we know it".
  none: 'No translation yet',
};

/**
 * Token classes per badge. Official is the only solid one — it is the strongest
 * claim available, so it is the only badge that looks like one.
 */
const TONES: Record<BadgeKind, string> = {
  publisher: 'bg-status-publisher-bg text-status-publisher',
  user: 'bg-status-user-bg text-status-user',
  official: 'bg-status-official text-status-official-foreground',
  reviewed: 'bg-status-reviewed-bg text-status-reviewed',
  ai: 'bg-status-ai-bg text-status-ai',
  human: 'bg-status-user-bg text-status-user',
  stale: 'bg-status-stale-bg text-status-stale border border-dashed border-current',
  none: 'bg-status-stale-bg text-status-stale border border-dashed border-current',
};

function Icon({ kind }: { kind: BadgeKind }) {
  const common = {
    viewBox: '0 0 24 24',
    width: 14,
    height: 14,
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    className: 'shrink-0',
  };
  switch (kind) {
    case 'publisher': // landmark
      return (
        <svg {...common}>
          <path d="M4 10h16M5 10v8M19 10v8M9 10v8M15 10v8M3 20h18M12 3l8 5H4l8-5z" />
        </svg>
      );
    case 'user': // person
      return (
        <svg {...common}>
          <path d="M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0" />
        </svg>
      );
    case 'official': // seal
      return (
        <svg {...common}>
          <path d="M12 3l2.2 1.6 2.7-.3 1 2.5 2.3 1.4-.7 2.6.7 2.6-2.3 1.4-1 2.5-2.7-.3L12 21l-2.2-1.6-2.7.3-1-2.5L3.8 15l.7-2.6-.7-2.6 2.3-1.4 1-2.5 2.7.3L12 3z" />
          <path d="M9 12l2 2 4-4" />
        </svg>
      );
    case 'reviewed': // check
      return (
        <svg {...common}>
          <path d="M20 6L9 17l-5-5" />
        </svg>
      );
    case 'human': // person
      return (
        <svg {...common}>
          <path d="M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0" />
        </svg>
      );
    case 'ai': // sparkle
      return (
        <svg {...common}>
          <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3zM18.5 16l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" />
        </svg>
      );
    case 'stale': // clock
    case 'none':
      return (
        <svg {...common}>
          <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 7v5l3.5 2" />
        </svg>
      );
  }
}

function Badge({ kind }: { kind: BadgeKind }) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-sm px-2',
        'text-[12px] font-semibold leading-none',
        TONES[kind],
      )}
    >
      <Icon kind={kind} />
      {LABELS[kind]}
    </span>
  );
}

/**
 * No translation has been produced at all.
 *
 * Shown beside the source badge the moment a khutbah is extracted, because
 * the two badges are independent and both are always present — verifying
 * where the Arabic came from says nothing about whether anyone has translated
 * or checked it, and showing only the source badge invites exactly that
 * conflation.
 */
export function NoTranslationBadge() {
  return <Badge kind="none" />;
}

/** Where the Arabic came from. One per khutbah. */
export function SourceBadge({ institutionKey }: { institutionKey: string }) {
  // A khutbah with no institution is a user upload; the publisher keys are
  // the two Awqaf ministries.
  const kind: BadgeKind = institutionKey ? 'publisher' : 'user';
  return <Badge kind={kind} />;
}

/**
 * How far this translation has been checked. One per translation.
 *
 * The order of these tests is the point. `publisher` outranks everything —
 * an institution's own translation is never relabelled. Then *reviewed*,
 * which since V15 means a person actually approved it rather than merely
 * that it was published. Then the honest default, split by who wrote it:
 * nobody has checked it.
 */
export function TranslationBadge({
  translation,
  stale = false,
}: {
  translation: TranslationReading;
  stale?: boolean;
}) {
  if (stale) {
    return <Badge kind="stale" />;
  }
  if (translation.origin === 'publisher') {
    return <Badge kind="official" />;
  }
  if (translation.reviewed) {
    return <Badge kind="reviewed" />;
  }
  // Unreviewed, so the remaining question is who WROTE it — and the answer is
  // not always a model. Badging a translator's own work "AI translation"
  // misstates its provenance just as badly as the reverse would.
  return <Badge kind={translation.origin === 'human' ? 'human' : 'ai'} />;
}

/**
 * Both badges together, which is the only arrangement the reader should ever
 * see — showing the source badge alone invites reading verification as
 * approval, and that is the conflation brief §5 forbids.
 */
export function ProvenanceRow({
  institutionKey,
  translation,
}: {
  institutionKey: string;
  translation: TranslationReading | null;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SourceBadge institutionKey={institutionKey} />
      {translation ? <TranslationBadge translation={translation} /> : null}
    </div>
  );
}
